import { TextButton, TextButtonLink } from '@creditornot/cb-button';
import { ChainLink } from '@creditornot/cb-icons';
import { MenuItemGroup } from '@creditornot/cb-menu';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRouteMatch } from 'react-router';
import styled, { ThemeConsumer } from 'styled-components';

import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';
import { useSelectionInfoContext } from 'modules/shared-menu/contexts/SelectionInfoContext';
import { isSharedMenuBootstrapping } from 'modules/shared-menu/utils';

import NavigationItem from './NavigationItem';
import NoCategoryNavItem from './NoCategoryNavItem';
import SharedMenuCategoryNavItems from './SharedMenuCategoryNavItems';
import { useSelectionData } from '../../../../hooks/useSelectionData';
import { useWoltSpecials } from '../../context/WoltSpecialsContext';
import { useVenueSelectionOverrides } from '../../hooks/useSelectionOverrides';
import { applyOverridesToOffer, getOfferCategory, getOfferOverrides } from '../../utils';

type Props = {
  className?: string;
  onClickAddCategory: () => void;
  woltcomUrl?: string;
};

const Root = styled.div`
  padding: 18px 24px 38px;
`;

const Section = styled.div`
  & + & {
    margin-block-start: 40px;
  }
`;

const List = styled(MenuItemGroup)`
  & + & {
    margin-block-start: 5px;
  }
`;

const AddCategory = styled(TextButton)`
  margin-block-start: 12px;
`;

const SharedMenuNavigation = ({ className, onClickAddCategory, woltcomUrl }: Props) => {
  const { url } = useRouteMatch();
  const { vlmeConfig } = useSelectionInfoContext();
  const isSharedVenue = !!vlmeConfig?.isSharedVenue;
  const menuPermissions = useOfferingMenuPermissionsContext();
  const { offers, categories, options, selection } = useSelectionData();
  const { woltSpecialsCategory } = useWoltSpecials();
  const { selectionOverrides } = useVenueSelectionOverrides();

  const bootstrapInProgress = useMemo(() => isSharedMenuBootstrapping(selection?.draft), [selection?.draft]);

  const bootstrapFailure = useMemo(() => selection?.draft?.bootstrapping?.status === 'failure', [selection?.draft]);

  const disabledItems = useMemo(
    () =>
      offers.filter(offer => {
        if (
          !isSharedVenue ||
          !selectionOverrides ||
          Object.keys(selectionOverrides).length === 0 ||
          Object.keys(selectionOverrides.offers).length === 0
        ) {
          return !offer.enabled.enabled;
        }

        const offerOverrides = getOfferOverrides(offer, selectionOverrides);
        const maybeOverriddenOffer = applyOverridesToOffer(offer, offerOverrides);
        return !maybeOverriddenOffer.enabled.enabled;
      }),
    [isSharedVenue, offers, selectionOverrides],
  );

  const disabledWoltSpecialsCount = useMemo(
    () =>
      woltSpecialsCategory
        ? disabledItems.filter(item =>
            woltSpecialsCategory.offer_bindings?.find(itemBinding => itemBinding.offer_id === item.id),
          ).length
        : 0,
    [disabledItems, woltSpecialsCategory],
  );
  const disabledRegularItemsCount = disabledItems.length - disabledWoltSpecialsCount;
  const disabledOptionsCount = useMemo(
    () => options.filter(option => option.values.find(optionValue => !optionValue.enabled.enabled)).length,
    [options],
  );
  const nonCategorisedItemsCount = offers.filter(offer => !getOfferCategory(categories, offer.id)).length;
  const allItemsCount = offers.length;
  const optionsCount = options.length;
  const disabledItemsCountToShow = disabledOptionsCount + disabledRegularItemsCount + disabledWoltSpecialsCount;
  const disableCategoryAdding = bootstrapInProgress || bootstrapFailure;

  return (
    <ThemeConsumer>
      {theme => (
        <Root className={className} dir={theme.cbDirection}>
          <Section>
            <List title={<FormattedMessage id="menu.collections" />}>
              <NavigationItem
                label={<FormattedMessage id="menu.allItems" />}
                numberOfItems={allItemsCount}
                href={url}
                exact
              />
              <NavigationItem
                label={<FormattedMessage id="menu.allOptions" />}
                numberOfItems={optionsCount}
                href={`${url}/options`}
                exact
              />
              <NavigationItem
                label={<FormattedMessage id="menu.allDisabled" />}
                numberOfItems={disabledItemsCountToShow}
                href={`${url}/all-disabled`}
                exact
              />
              <NoCategoryNavItem numberOfItems={nonCategorisedItemsCount} href={`${url}/no-category`} exact />
            </List>
          </Section>
          <Section>
            <SharedMenuCategoryNavItems categories={categories} menuEditorRootUrl={url} />
            {!isSharedVenue && menuPermissions.contentEdit && (
              <AddCategory onClick={onClickAddCategory} disabled={disableCategoryAdding}>
                <FormattedMessage id="menu.addCategory" />
              </AddCategory>
            )}
          </Section>
          {woltcomUrl && (
            <Section>
              <TextButtonLink href={woltcomUrl} target="blank" icon={<ChainLink />} iconPosition="right">
                <FormattedMessage id="menu.viewOnWoltcom" />
              </TextButtonLink>
            </Section>
          )}
        </Root>
      )}
    </ThemeConsumer>
  );
};

export default React.memo(SharedMenuNavigation);
